import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import CategoryHead from '../components/CategoryHead';
import BooksGrid from '../components/BooksGrid';
import { CategoryGrid } from '../components/CategoryGrid';
import { HomePageBanner } from '../components/HomePageBanner';
import NewsCarousel from '../components/NewsCarousel';
import { PublisherSection } from '../components/PublisherSection';
import { HorizontalAd } from '../components/HorizontalAd';

const HomePageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(to right, #d1e3df, #b2d0cd);
`;

const ContentContainer = styled.main`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: white;
  padding: 0 10px;

  @media (min-width: 768px) {
    padding: 0 20px;
  }
`;

const HomePage: React.FC = () => {
  const [authors, setAuthors] = useState<any[]>([]);
  const [publishers, setPublishers] = useState<any[]>([]);
  const [newestBooksRaw, setNewestBooksRaw] = useState<any[]>([]);
  const [popularBooksRaw, setPopularBooksRaw] = useState<any[]>([]);
  const [taggedNewBooks, setTaggedNewBooks] = useState<any[]>([]);
  const [news, setNews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const enrichBooksData = (
    books: any[],
    authors: any[],
    publishers: any[]
  ) => {
    return books.map(book => {
      const author = authors.find(a => String(a.author_id) === String(book.author_id));
      const publisher = publishers.find(p => String(p.publisher_id) === String(book.publisher_id));

      return {
        ...book,
        author_name: author?.author_name || 'Невідомий автор',
        publisher_name: publisher?.publisher_name || 'Невідомий видавець',
        book_quantity: book.book_quantity ?? 0
      };
    });
  };

  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        const [authorsRes, publishersRes, newsRes, rssRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BASE_API_URL}/authors`),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/publishers`),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/news`),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/rss`)
        ]);
        const authorsData = await authorsRes.json();
        const publishersData = await publishersRes.json();
        const newsData = await newsRes.json();
        const rssData = await rssRes.json();
        
        setAuthors(authorsData);
        setPublishers(publishersData);
        setNews([...newsData, ...rssData]);
      } catch (error) {
        console.error('Помилка при завантаженні авторів/видавців/новин:', error);
      }
    };

    fetchMetaData();
  }, []);

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const [newestRes, popularRes, taggedNewRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BASE_API_URL}/books?limit=3`),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/books/popularity/top?limit=3`),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/books/tagged_new`)
        ]);
        const newestData = await newestRes.json();
        const popularData = await popularRes.json();
        const taggedNewData = await taggedNewRes.json();

        setNewestBooksRaw(newestData);
        setPopularBooksRaw(popularData);
        setTaggedNewBooks(taggedNewData);
      } catch (error) {
        console.error('Помилка при завантаженні книг:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBooks();
  }, []);

  const newestBooks = useMemo(() => {
    return enrichBooksData(newestBooksRaw, authors, publishers);
  }, [newestBooksRaw, authors, publishers]);

  const popularBooks = useMemo(() => {
    return enrichBooksData(popularBooksRaw, authors, publishers);
  }, [popularBooksRaw, authors, publishers]);

  const firstNews = news.length > 0 ? news[0] : null;

  if (loading || authors.length === 0 || publishers.length === 0) {
    return (
      <HomePageContainer>
        <ContentContainer>
          <div style={{ padding: '50px', textAlign: 'center' }}>
            Завантаження...
          </div>
        </ContentContainer>
      </HomePageContainer>
    );
  }

  return (
    <HomePageContainer>
      <ContentContainer>
        {firstNews && <HomePageBanner newsItem={firstNews} />}
        <CategoryGrid />
        {news.length >= 3 && <NewsCarousel items={news} />}
        
        <CategoryHead title='НОВИНКИ:' />
        <BooksGrid books={taggedNewBooks.filter(book => book.book_quantity > 0)} />

        <CategoryHead title='ПОПУЛЯРНЕ:' />
        <BooksGrid books={popularBooks} />

        <PublisherSection />
        <HorizontalAd imageUrl={`${process.env.PUBLIC_URL}/media/horizontal_ad.png`} />
      </ContentContainer>
    </HomePageContainer>
  );
};

export default HomePage;